/**
 * Explore More Step Component
 *
 * Step 5: Show next steps with action cards.
 *
 * @module components/onboarding/ExploreMoreStep
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import {
  Users,
  CreditCard,
  BookOpen,
  LayoutDashboard,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { links } from '@/lib/config';

// =============================================================================
// Types
// =============================================================================

export interface ExploreMoreStepProps {
  /** Callback to mark onboarding as complete */
  onComplete: () => Promise<void>;
}

interface ActionCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: () => void;
  actionLabel: string;
  external?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function ExploreMoreStep({ onComplete }: ExploreMoreStepProps) {
  const navigate = useNavigate();

  const handleAction = async (action: () => void) => {
    await onComplete();
    action();
  };

  const actionCards: ActionCard[] = [
    {
      icon: <BookOpen className="h-6 w-6 text-brand-600" />,
      title: 'Explore the docs',
      description: 'Learn about all available APIs and features.',
      action: () => handleAction(() => window.open(links.docs, '_blank')),
      actionLabel: 'Open docs',
      external: true,
    },
    {
      icon: <Users className="h-6 w-6 text-brand-600" />,
      title: 'Invite team members',
      description: 'Collaborate with your team on API projects.',
      action: () => handleAction(() => navigate('/settings/team')),
      actionLabel: 'Manage team',
    },
    {
      icon: <CreditCard className="h-6 w-6 text-brand-600" />,
      title: 'Upgrade for more power',
      description: 'Unlock higher limits and premium features.',
      action: () => handleAction(() => navigate('/settings/billing')),
      actionLabel: 'View plans',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          🎉 You're all set!
        </h2>
        <p className="text-gray-500 mt-2">
          Your API key is working. Here's what you can do next.
        </p>
      </div>

      {/* Action cards */}
      <div className="grid gap-4">
        {actionCards.map((card) => (
          <button
            key={card.title}
            onClick={card.action}
            className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:border-brand-300 hover:shadow-sm transition-all text-left group"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-brand-50 rounded-lg flex-shrink-0">
              {card.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">{card.title}</p>
              <p className="text-sm text-gray-500">{card.description}</p>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-700">
              {card.actionLabel}
              {card.external ? (
                <ExternalLink className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Primary CTA */}
      <div className="pt-2">
        <Button
          fullWidth
          size="lg"
          onClick={async () => {
            await onComplete();
            navigate('/overview');
          }}
          rightIcon={<LayoutDashboard className="h-5 w-5" />}
        >
          Take me to the dashboard
        </Button>
      </div>
    </div>
  );
}
